import type React from 'react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, ThemeProvider } from 'styled-components';
import {
    type FindStoreResult,
    type FindStoreResultViewModel,
    type ProductInfoModel,
    type ProductStoreStockViewModel,
    SetUserPreferredStore,
    type StoreParcelShopResult,
    UpdateBasketLineStore
} from 'autogen/swagger/all';
import { useUpdateBasketLineStoreMutation } from 'autogen/swagger/all/hooks';
import { ShowMoreStoresButtonText } from 'autogen/translation-keys/trans-website-checkout-delivery-parcelshop';
import { NoStoresError } from 'autogen/translation-keys/trans-website-productstorestock';
import { Button, ButtonWithSpinner } from 'components/Button';
import { updateBasket } from 'components/basket/shared/basket';
import HighlightedText from 'components/generic/HighlightedText';
import ProductActionRenderer from 'components/generic/productCTA';
import type { IProductActionButton } from 'components/generic/productCTA/shared/types';
import { Typography } from 'components/generic/typography';
import ActionFooter from 'components/quickView/components/ActionFooter';
import StoreCard from 'components/StoreCard';
import StatusRenderer from 'components/StoreCard/components/StatusRenderer';
import useFeatureToggles from 'hooks/user/useFeatureToggles';
import { removeListStyling } from 'modules/helpers/mixins';
import TrackingContext from 'modules/tracking/GA4/helpers/TrackingContext';
import { OnSelectParcelPickupContext } from '../shared/helpers';
import { useFindInStoreQuickView } from '../shared/hooks';
import useThemeHandler from '../themes/useThemeHandler';

const Container = styled.div<{ $showButton?: boolean; $isLoading: boolean }>`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing16};
    min-width: 0;
    opacity: ${({ $isLoading }) => ($isLoading ? 0.4 : 1)};

     ${({ $showButton }) =>
         $showButton &&
         css`
            margin-bottom: ${({ theme }) => theme.Spacing88};
        `}
`;

const AdditionalContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing4};
`;

const StyledActionFooter = styled(ActionFooter)`
    position: fixed;
    left: 0;
    bottom: 0;
    width: 100%;
`;

const StyledButton = styled(Button)`
    width: 100%;
`;

const ParcelPickupList = styled.ul`
    ${removeListStyling}

    display: grid;
    gap: ${({ theme }) => theme.Spacing16};
    min-width: 0;
`;

interface IStoreList extends FindStoreResult {
    translations: {
        chooseStoreButtonText: string;
    };
    initialPreferredStore?: ProductStoreStockViewModel['initialPreferredStore'];
    showSelectParcelShop?: boolean;
    numberOfStores?: FindStoreResultViewModel['numberOfStores'];
    incrementPage?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => Promise<any>;
    productInfo: ProductInfoModel;
    clickAndCollectTimeTag?: string;
    isBasket?: boolean;
    isLoading: boolean;
}

export default function StoreList({
    parcelShops,
    searchData,
    numberOfStores,
    translations,
    initialPreferredStore,
    showSelectParcelShop,
    incrementPage,
    productInfo,
    clickAndCollectTimeTag,
    isBasket,
    isLoading
}: IStoreList): JSX.Element {
    const { chooseStoreButtonText } = translations;
    const [activeElementId, setActiveElementId] = useState<StoreParcelShopResult['id'] | undefined>(
        initialPreferredStore?.storeId
    );
    const [activeElement, setActiveElement] = useState<StoreParcelShopResult>();
    const onSelectParcelPickup = useContext(OnSelectParcelPickupContext);
    const [, toggle] = useFindInStoreQuickView();
    const { clickAndCollectTimePromiseInLabelDesignEnabled, clickAndCollectDisplayOnProductPage } = useFeatureToggles();
    const { t } = useTranslation();
    const { ButtonStyling } = useThemeHandler() as any;

    useUpdateBasketLineStoreMutation();

    function onChooseParcelPickup() {
        if (activeElementId) {
            SetUserPreferredStore({ storeId: activeElementId }).then(() => {
                if (onSelectParcelPickup) onSelectParcelPickup();
            });
        }
    }

    async function onChooseParcelPickupInBasket() {
        if (activeElementId) {
            await UpdateBasketLineStore({ productId: productInfo.productId, storeId: activeElementId });
            updateBasket();
            toggle({ isOpen: false });
        }
    }

    const productActionButtonModel: IProductActionButton['model'] = {
        action: 'BuyNow',
        trackingContext: { eventCategory: 'storeStockCheckerQuickView', type: 'Page' },
        productId: productInfo.productId,
        externalProductId: productInfo.productId,
        productNavigationLink: {
            $type: 'ProductLinkAction',
            productId: productInfo?.productId,
            url: '',
            noFollow: false,
            openInNewWindow: false
        },
        fromPowerstep: false,
        addToBasketType: 'Default',
        showPowerstepOnAddToBasket: true,
        storeId: activeElementId
    };

    const statusAsTag = clickAndCollectTimePromiseInLabelDesignEnabled && clickAndCollectTimeTag;

    return (
        <ThemeProvider theme={{ ButtonStyling } as any}>
            <Container $showButton={activeElement?.isClickAndCollect || showSelectParcelShop} $isLoading={isLoading}>
                {numberOfStores && (
                    <Typography.Body size="M" strong as="span">
                        {numberOfStores}
                    </Typography.Body>
                )}
                {parcelShops.length === 0 && (
                    <HighlightedText color="ColorSystemAttention2">{t(NoStoresError)}</HighlightedText>
                )}
                <ParcelPickupList>
                    {parcelShops.map((parcelShop) => {
                        const { id } = parcelShop;

                        return (
                            <StoreCard
                                key={id}
                                model={parcelShop}
                                setSelectedId={() => setActiveElementId(id)}
                                setSelectedItem={() => setActiveElement(parcelShop)}
                                nameType="store"
                                selected={activeElementId === id}
                                {...(parcelShop.isClickAndCollect && statusAsTag
                                    ? { statusTag: clickAndCollectTimeTag }
                                    : {})}
                                showUnavailableStatus={true}
                                additionalContentRenderer={() => (
                                    <AdditionalContentContainer>
                                        <StatusRenderer
                                            status={parcelShop.productStockStatus}
                                            showUnavailableStatus
                                            showText
                                        />
                                        {parcelShop.storeStockText}
                                    </AdditionalContentContainer>
                                )}
                            />
                        );
                    })}
                </ParcelPickupList>
                {!searchData?.isLastPage && incrementPage && (
                    <ButtonWithSpinner variant="primaryText" size="lg" onClick={incrementPage}>
                        {t(ShowMoreStoresButtonText)}
                    </ButtonWithSpinner>
                )}
                {showSelectParcelShop && !!parcelShops.length && (
                    <StyledActionFooter size="lg">
                        <StyledButton
                            onClick={isBasket ? onChooseParcelPickupInBasket : onChooseParcelPickup}
                            disabled={!activeElement}
                            size="lg"
                        >
                            {chooseStoreButtonText}
                        </StyledButton>
                    </StyledActionFooter>
                )}
                {!showSelectParcelShop && activeElement?.isClickAndCollect && clickAndCollectDisplayOnProductPage && (
                    <TrackingContext
                        category="ecommerce"
                        context="product_page"
                        eventParams={[{ param_name: 'is_click_and_collect', param_value: true }]}
                    >
                        <StyledActionFooter size="lg">
                            <ProductActionRenderer size="lg" model={productActionButtonModel} />
                        </StyledActionFooter>
                    </TrackingContext>
                )}
            </Container>
        </ThemeProvider>
    );
}
